import { CSSProperties, useContext, useMemo, useState } from "react";

import CloseIcon from "~/icons/CloseIcon";
import ThemeContext from "~/contexts/ThemeContext";

import styles from "./InfoBar.module.css";
import { getButtonStyles } from "~/theme/models/themeModels";
import { Size } from "~/entities/templateEntities";
import TimeIcon from "~/icons/TimeIcon";
import { formatDay, formatTime } from "~/helpers/dateHelper";
import { toZonedTime } from "date-fns-tz";
import { OrderFulfillmentType } from "~/entities/orderEntities";
import AuthContext from "~/contexts/AuthContext";
import LoyaltyBalance from "~/modules/loyalty/components/LoyaltyBalance/LoyaltyBalance";
import SiteContext from "~/contexts/SiteContext";
import OrderContext from "~/contexts/OrderContext";
import LocationIcon from "~/icons/LocationIcon";
import ArrowRightIcon from "~/icons/ArrowRightIcon";
import { appConfig } from "~/configs/appConfig";

const InfoBar: React.FC = () => {
  const [hideAuthSection, setHideAuthSection] = useState(false);

  const { template } = useContext(ThemeContext);
  const { authenticatedUser, isAuthEnabled, useComoAuth, setIsAuthDialogOpen } =
    useContext(AuthContext);
  const { site } = useContext(SiteContext);
  const {
    orderSummary,
    orderType,
    openOrderTimeModal,
    asapWait,
    deliveryInfo,
  } = useContext(OrderContext);

  const loyaltyBalance = authenticatedUser?.loyaltyBalance;

  const timeText =
    site && orderSummary.requestedReadyTime
      ? `${formatDay(
          toZonedTime(orderSummary.requestedReadyTime, site.timeZone),
        )}, ${formatTime(
          toZonedTime(orderSummary.requestedReadyTime, site.timeZone),
        )}`
      : asapWait
        ? `Today, ASAP (About ${asapWait} Minutes)`
        : "Today, ASAP";

  // Styles
  const menuBackgroundColor = template.menuScreen.general.backgroundColour;
  // Texts
  const loginOrSignUp = template.loyalty.text.loginOrSignUp;

  const backgroundColor = template.infoBar.general.backgroundColour;
  const color = template.infoBar.general.fontColour;

  // Need to move to template
  const orderTimeChangeButtonColor =
    template.infoBar.orderTime.changeButtonColour;

  const buttonStyles: CSSProperties | undefined = useMemo(() => {
    const baseStyles = getButtonStyles(
      template.infoBar.authButton.colour,
      template.infoBar.authButton.colour,
      template.infoBar.authButton.fontColour,
      Size.S,
      template.infoBar.authButton.style,
    );
    const hoverStyles = {
      "--hover-auth-button-colour": template.infoBar.authButton.hoverColour,
      "--hover-auth-button-border-colour":
        template.infoBar.authButton.hoverColour,
      "--hover-auth-button-font-colour":
        template.infoBar.authButton.hoverFontColour,
    };

    return {
      ...baseStyles,
      ...hoverStyles,
    };
  }, [
    template.infoBar.authButton.colour,
    template.infoBar.authButton.fontColour,
    template.infoBar.authButton.hoverColour,
    template.infoBar.authButton.hoverFontColour,
    template.infoBar.authButton.style,
  ]);

  return (
    site && (
      <>
        {/* Desktop Info Bar */}
        <div
          className={styles.desktopWrapper}
          style={{
            backgroundColor,
            color,
          }}
        >
          <div className={styles.desktopInnerWrapper}>
            <div
              className={styles.orderTimeDesktop}
              style={{ color }}
              onClick={() => openOrderTimeModal()}
            >
              <div className={styles.orderDesktopDetails}>
                <LocationIcon />
                <span>
                  {orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY
                    ? "Delivery"
                    : "Pickup"}{" "}
                  from {site?.siteName}
                </span>
                <span>|</span>
                <span>{timeText}</span>
                <div
                  className={styles.orderTimeChangeButton}
                  style={{
                    color: orderTimeChangeButtonColor,
                    fontSize: "initial",
                  }}
                >
                  Change
                </div>
                <ArrowRightIcon />
              </div>
              {orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY &&
                deliveryInfo && (
                  <div className={styles.orderDeliveryAddress}>
                    To: {deliveryInfo?.address.address1}
                  </div>
                )}
            </div>
            {isAuthEnabled && (
              <div className={styles.loyalty}>
                {!authenticatedUser ? (
                  !appConfig.WebView && (
                    <div
                      className={styles.authButton}
                      style={buttonStyles}
                      onClick={() => setIsAuthDialogOpen(true)}
                    >
                      {loginOrSignUp}
                    </div>
                  )
                ) : (
                  <LoyaltyBalance fontColor={color} />
                )}
              </div>
            )}
          </div>
        </div>
        {/* Mobile Info Bar */}
        <div
          className={styles.mobileWrapper}
          style={{
            backgroundColor: menuBackgroundColor,
          }}
        >
          <div
            className={styles.mobileSection}
            style={{ backgroundColor, color }}
            onClick={() => openOrderTimeModal()}
          >
            <div className={styles.orderTime} style={{ color }}>
              <div className={styles.mobileTimeWrapper}>
                <div className={styles.pickupHeader}>
                  <span>
                    {orderType?.fulfillmentType ===
                    OrderFulfillmentType.DELIVERY
                      ? "Delivery"
                      : "Pickup"}{" "}
                    from{" "}
                    <span className={styles.siteName}>{site?.siteName}</span>
                  </span>
                </div>
                {orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY &&
                  deliveryInfo && (
                    <div className={styles.orderDeliveryAddress}>
                      To: {deliveryInfo?.address.address1}
                    </div>
                  )}
                <div className={styles.timeSection}>
                  <div className={styles.pickupTime}>
                    <TimeIcon color={color} />
                    <span>{timeText}</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={styles.orderTimeChangeButton}
              style={{ color: orderTimeChangeButtonColor }}
            >
              Change
            </div>
          </div>
          {isAuthEnabled &&
            useComoAuth &&
            !hideAuthSection &&
            (authenticatedUser || !appConfig.WebView) && (
              <div
                className={styles.mobileSection}
                style={{ backgroundColor, color }}
              >
                <div className={styles.mobileAuthLabel}>
                  {authenticatedUser ? (
                    <LoyaltyBalance fontColor={color} />
                  ) : (
                    <span
                      className={styles.mobileAuthLink}
                      onClick={() => setIsAuthDialogOpen(true)}
                    >
                      {loginOrSignUp}
                    </span>
                  )}
                </div>
                {loyaltyBalance == undefined && (
                  <div
                    className={styles.mobileCloseIconWrapper}
                    onClick={() => setHideAuthSection(true)}
                  >
                    <CloseIcon />
                  </div>
                )}
              </div>
            )}
        </div>
      </>
    )
  );
};

export default InfoBar;
